<script lang="ts" setup>
  import { ref, watch, computed, type Ref } from 'vue';
  import { type Shared, useTranslator, useShared } from '@othercommerce/foundation';
  import { useBreakpoints, useScrollDetector } from '@storefront/storefront';

  type LayoutSharedState = Partial<Shared> & {
    comparator: Object[];
    menu: {
      menu_top: Object[]
    };
  }

  const headerBottom = ref();

  const scroll = ref(false);
  const show = ref(false);
  const searchMobileBar = ref(false);

  const breakpoints = useBreakpoints();
  const shared = useShared() as Ref<LayoutSharedState>;
  const translator = useTranslator();

  const name = computed(() => {
    return shared.value.appName || 'Promoznawcy';
  });

  const phone = computed(() => {
    return shared.value.department?.phone;
  });

  const email = computed(() => {
    return shared.value.department?.email;
  });

  const warehouses = computed(() => {
    return (shared.value.availablePickupPoints ?? []);
  });

  const label = computed(() => {
    return translator.trans('SHOP.COMMON.ARIA.COMPANY_LOGO_EXTENDED', { name: name.value });
  });

  const isComparator = computed(() => Object.keys(shared.value.comparator).length > 0);

  useScrollDetector({
    threshold: 8,
    callback: ({ up }) => {
      if (up) {
        headerBottom.value.classList.add('header__bottom--open');
        scroll.value = false;
      } else {
        headerBottom.value.classList.remove('header__bottom--open');
        scroll.value = true;
      }
    },
  });

  watch(scroll, () => {
    if (!scroll.value) {
      show.value = false;
      searchMobileBar.value = false;
    }
  });
</script>
<template>
  <SfSkipToContentButton />
  <SfSkipToContentButton href="#search-input">
    {{ $t('SHOP.COMMON.ACTION.GO_TO_SEARCH') }}
  </SfSkipToContentButton>
  <SfSkipToContentButton class="skip-to-content-categories" href="#categories-btn">
    {{ $t('SHOP.COMMON.ACTION.GO_TO_CATEGORIES') }}
  </SfSkipToContentButton>
  <SfSkipToContentButton class="bottom-navigation" href="#bottom-navigation">
    {{ $t('SHOP.COMMON.ACTION.GO_TO_BOTTOM_MENU') }}
  </SfSkipToContentButton>
  <SfSkipToContentButton href="#footer">
    {{ $t('SHOP.COMMON.ACTION.GO_TO_FOOTER') }}
  </SfSkipToContentButton>
  <SfSkipToContentButton v-if="isComparator" href="#comparator-bar">
    {{ $t('SHOP.COMMON.ACTION.GO_TO_COMPARATOR_BAR') }}
  </SfSkipToContentButton>
  <header :class="{ 'search-mobile-active': searchMobileBar }" class="header">
    <div class="header__actions">
      <div class="container header__actions-container">
        <address class="header__actions-contact">
          <a v-if="phone" :href="`tel:${phone}`" class="header__actions-link">
            <SfIconPhone aria-hidden="true" />
            {{ phone }}
          </a>
          <a v-if="email" :href="`mailto:${email}`" class="header__actions-link">
            <SfIconEnvelope aria-hidden="true" />
            {{ email }}
          </a>
        </address>
        <div class="header__actions-activities">
          <VatSwitcher />
          <LocalizationDropdown />
          <HeaderAccount />
        </div>
      </div>
    </div>
    <div class="header__top">
      <div class="container header__top-container">
        <RouterLink :href="$route('web.homepage.index')" class="header__top-logo">
          <SfLogo :aria-label="label" class="header__top-img" role="img" />
        </RouterLink>
        <HeaderSearch v-if="breakpoints.lg" :placeholder="$t('SHOP.COMMON.LABEL.FIND_PRODUCT')" class="header__top-search" />
        <div class="header__top-icons">
          <HeaderWarehouse v-if="warehouses.length > 1" :warehouses />
          <HeaderFavorite v-if="breakpoints.lg" />
          <MiniCart />
        </div>
      </div>
    </div>
    <div ref="headerBottom" class="header__bottom header__bottom--open">
      <div class="container header__bottom-inner">
        <div class="header__bottom-wrapper">
          <HeaderSearch v-if="!breakpoints.lg" :label="$t('SHOP.COMMON.LABEL.FIND_PRODUCT')" :placeholder="$t('SHOP.COMMON.LABEL.FIND_PRODUCT')" />
          <Categories v-if="breakpoints.lg" />
          <nav v-if="breakpoints.lg" :aria-label="$t('SHOP.COMMON.ARIA.MAIN_MENU')" class="navigation">
            <ul>
              <HeaderNavItem v-for="item in shared.menu.menu_top" :depth="0" :item="item" />
            </ul>
          </nav>
        </div>
      </div>
    </div>
  </header>
</template>
<style lang="scss">
  :root {
    --sf-header-logo-height: 35px;
    --sf-header-logo-max-height: 35px;

    @media (max-width: 992px) {
      --sf-header-logo-width: 120px;
      --sf-header-logo-height: 30px;
      --sf-header-logo-max-height: 30px;
    }
  }
</style>
<style lang="scss" scoped>
  @keyframes show-overflow {
    from {
      overflow: hidden;
    }
    to {
      overflow: visible;
    }
  }

  .icon-hamburger {
    font-size: 16px;
  }

  .fade-down-enter-active,
  .fade-down-leave-active {
    transition: var(--primary-transition-fade);
  }

  .fade-down-enter-from,
  .fade-down-leave-to {
    opacity: 0;
    transform: translateY(-5px);
  }

  .search-mobile-active.header {
    box-shadow: none;
  }

  .bottom-navigation {
    display: none;
  }

  .mobile-search {
    position: fixed;
    top: 49px;
    width: 100%;
    z-index: 997;
    padding: 5px 15px 10px;
    background-color: var(--color-white);
    box-shadow: var(--primary-box-shadow-200);
  }

  .header {
    position: sticky;
    top: 0;
    right: 0;
    z-index: 999;
    width: 100%;
    background: var(--color-white);
    box-shadow: var(--secondary-box-shadow-200);
    transition: var(--primary-transition);


    &__top-search {
      width: 50%;
    }

    &__actions {
      padding: 0;
      background: var(--color-light-background);

      @media (max-width: 992px) {
        display: none;
      }
    }

    &__actions-container {
      display: flex;
      justify-content: space-between;
      padding: 0.125rem 0;
    }

    &__actions-contact {
      display: flex;
      gap: 1.5rem;

      a {
        text-decoration: underline;
        text-decoration-color: transparent;
        transition: text-decoration-color 0.2s ease-out;
      }

      @media(hover: hover) {
        a:hover {
          text-decoration-color: var(--primary-color);
        }
      }
    }

    &__actions-activities {
      display: flex;
      gap: 1.5rem;
    }

    &__actions-link {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      font-size: 0.875rem;
    }

    &__top-container {
      position: relative;
      display: flex;
      justify-content: space-between;
      align-items: center;
      column-gap: 2rem;
      padding: 0.75rem 0;
    }

    &__top-img {
      width: 100%;
      max-width: 250px;
      height: var(--sf-header-logo-height);
      max-height: var(--sf-header-logo-max-height);
      object-fit: contain;
    }

    &__top-logo {
      display: flex;
      align-items: center;
    }

    &__top-icons {
      display: flex;
      align-items: center;
      justify-content: flex-end;
      column-gap: 0.25rem;
    }
  }

  @media (max-width: 992px) {
    .header {
      &__top-container {
        padding: 0.625rem;
        gap: 0.75rem;
      }

      &__top-img {
        width: var(--sf-header-logo-width);
      }
    }

    .skip-to-content-categories {
      display: none;
    }
    .bottom-navigation {
      display: inline-flex;
    }
  }

  .header__bottom {
    display: grid;
    width: 100%;
    min-width: 0;
    grid-template-rows: 0fr;
    transition: grid-template-rows ease-in-out 100ms;
    border-top: 1px solid var(--color-grey);

    @media(max-width: 992px) {
      border-top: none;
    }

    &--open {
      grid-template-rows: 1fr;

      .header__bottom-inner {
        animation: show-overflow ease-in-out 100ms;
        overflow: visible;
      }
    }
  }

  .header__bottom-inner {
    position: relative;
    display: flex;
    width: 100%;
    min-width: 0;
    overflow: hidden;
  }

  .header__bottom-wrapper {
    display: flex;
    width: 100%;
    min-width: 0;

    @media(max-width: 992px) {
      display: block;
      padding: 0 0.625rem 0.5rem;
    }
  }

  .navigation {
    position: relative;
    padding-left: 3rem;
    width: 100%;

    ul {
      display: flex;
      gap: 3rem;
      padding: 0;
      margin: 0;
      list-style: none;

      li.nav-item {
        position: relative;

        :deep(.nav-item-arrow) {
          transform: rotate(0);
          font-size: 0.75rem;
          margin-left: 0.5rem;
        }

        :deep(> .children-wrapper) {
          top: 100%;
          left: 0;
          right: unset;
          height: auto;
          padding: 0.5rem;
          min-width: 300px;
          border-radius: 0;

          a {
            font-weight: 600;
            padding: 0.5rem 0;
          }

          .nav-item.level-2 .children-wrapper {
            display: flex;
            flex-direction: column;
          }

          .children {
            display: flex;
            flex-direction: column;
            gap: 0.25rem;
            padding-left: 1rem;
          }

          @media(max-width: 1200px) {
            left: unset;
            right: 0;
          }
        }
      }
    }

    :deep(.level-0) {
      & > a {
        line-height: 22px;
        padding: 0.875rem 0;
        color: var(--color-black);
        transition: var(--primary-transition);
        z-index: 2;
        font-weight: 500;


        &:not(.active):hover {
          color: var(--primary-color);
        }

        &.active {
          position: relative;
          display: inline-block;
          color: var(--primary-color);
          font-weight: var(--font-weight-semi);
        }
      }
    }
  }

  @media (max-width: 1200px) {
    .navigation ul {
      gap: 30px;
    }
  }

  @media (max-width: 992px) {
    .navigation {
      padding-left: 10px;

      ul {
        gap: 14px;
        font-size: 15px;
      }
    }
  }
</style>
